import {Injectable} from '@angular/core';
import {AbstractControl, ValidationErrors, ValidatorFn, FormGroup} from '@angular/forms';
import {phoneMask, cpfMask} from '../../uteis/form-validations';

@Injectable({
    providedIn: 'root'
})
export class ValidatorService {

    /** Chaves de tradução para erros de senha (auth.form.*). */
    private static readonly PASSWORD_ERROR_KEYS = {
        required: 'auth.form.passwordRequired',
        minLength: 'auth.form.passwordMinLength',
        number: 'auth.form.passwordNumber',
        uppercase: 'auth.form.passwordUppercase',
        special: 'auth.form.passwordSpecial',
    } as const;

    /**
     * Validação de senha forte (retorna chaves i18n para exibição com Transloco).
     */
    passwordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) {
                return { passwordInvalid: ValidatorService.PASSWORD_ERROR_KEYS.required };
            }

            const hasNumber = /\d/.test(value);
            const hasUpperCase = /[A-Z]/.test(value);
            const hasSpecialChar = /[!@#$%^&*()_+{}\[\]:;<>,.?/~\\|-]/.test(value);
            const hasMinLength = value.length >= 12;

            if (!hasMinLength) {
                return { passwordInvalid: ValidatorService.PASSWORD_ERROR_KEYS.minLength };
            }
            if (!hasNumber) {
                return { passwordInvalid: ValidatorService.PASSWORD_ERROR_KEYS.number };
            }
            if (!hasUpperCase) {
                return { passwordInvalid: ValidatorService.PASSWORD_ERROR_KEYS.uppercase };
            }
            if (!hasSpecialChar) {
                return { passwordInvalid: ValidatorService.PASSWORD_ERROR_KEYS.special };
            }

            return null;
        };
    }

    /**
     * Validação de confirmação de senha
     */
    passwordMatchValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control as FormGroup;
            if (!formGroup) {
                return null;
            }

            const senha = formGroup.get('senha');
            const confirmacaoSenha = formGroup.get('confirmacao_senha');

            if (!senha || !confirmacaoSenha) {
                return null;
            }

            if (!senha.value && !confirmacaoSenha.value) {
                return null;
            }

            if (senha.value !== confirmacaoSenha.value) {
                confirmacaoSenha.setErrors({passwordMismatch: true});
                return {passwordMismatch: true};
            } else {
                if (confirmacaoSenha.errors?.['passwordMismatch']) {
                    const errors = {...confirmacaoSenha.errors};
                    delete errors['passwordMismatch'];
                    confirmacaoSenha.setErrors(Object.keys(errors).length > 0 ? errors : null);
                }
                return null;
            }
        };
    }

    /**
     * Validação de telefone com DDI
     */
    phoneWithDDIValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) {
                return null;
            }

            const numbers = value.replace(/\D/g, '');

            let ddi = '';
            let restOfNumber = numbers;

            // Prioriza DDI 55 (Brasil)
            if (numbers.startsWith('55')) {
                ddi = '55';
                restOfNumber = numbers.substring(2);
            }
            // Tenta DDI de 3 dígitos
            else if (numbers.length >= 3 && /^[2-9]\d{2}/.test(numbers.substring(0, 3))) {
                ddi = numbers.substring(0, 3);
                restOfNumber = numbers.substring(3);
            }
            // Tenta DDI de 2 dígitos
            else if (numbers.length >= 2 && /^[1-9]\d/.test(numbers.substring(0, 2))) {
                ddi = numbers.substring(0, 2);
                restOfNumber = numbers.substring(2);
            }
            // Tenta DDI de 1 dígito
            else if (numbers.length >= 1 && /^[17]/.test(numbers.substring(0, 1))) {
                ddi = numbers.substring(0, 1);
                restOfNumber = numbers.substring(1);
            }

            // Se não detectou DDI, valida como número sem DDI (DDD + 8/9 dígitos)
            if (!ddi) {
                if (numbers.length < 10) {
                    return {invalidPhone: 'Número de telefone incompleto. Inclua DDD e o número (10 ou 11 dígitos).'};
                }
                const ddd = numbers.substring(0, 2);
                const num = numbers.substring(2);

                if (!/^[1-9]{2}$/.test(ddd)) {
                    return {invalidPhone: 'DDD inválido. O DDD deve ter 2 dígitos entre 11 e 99.'};
                }
                if (!/^\d{8,9}$/.test(num)) {
                    return {invalidPhone: 'Número de telefone inválido. Deve ter 8 ou 9 dígitos após o DDD.'};
                }
                return null;
            }

            // Validação com DDI
            if (!/^[1-9]\d{0,2}$/.test(ddi)) {
                return {invalidPhone: 'DDI inválido. O número deve começar com um código de país válido (ex: 55 para Brasil).'};
            }

            // Validação específica para Brasil (DDI 55)
            if (ddi === '55') {
                if (restOfNumber.length < 10) {
                    return {invalidPhone: 'Número brasileiro incompleto. Formato esperado: 55 + DDD (2 dígitos) + número (8 ou 9 dígitos). Exemplo: 5511941566889'};
                }
                const ddd = restOfNumber.substring(0, 2);
                const num = restOfNumber.substring(2);

                if (!/^[1-9]{2}$/.test(ddd)) {
                    return {invalidPhone: 'DDD inválido. O DDD deve ter 2 dígitos entre 11 e 99.'};
                }
                if (!/^\d{8,9}$/.test(num)) {
                    return {invalidPhone: 'Número de telefone inválido. Deve ter 8 ou 9 dígitos após o DDD.'};
                }
            } else {
                // Validação para outros DDIs
                if (restOfNumber.length < 7 || restOfNumber.length > 15) {
                    return {invalidPhone: 'Número de telefone inválido. Deve ter entre 7 e 15 dígitos após o DDI.'};
                }
            }

            return null;
        };
    }

    /**
     * Aplica máscara de telefone
     */
    applyPhoneMask(value: string, forceUseDDI?: boolean): string {
        return phoneMask(value, forceUseDDI);
    }

    /**
     * Aplica máscara de CPF/CNPJ
     */
    applyCpfMask(value: string): string {
        return cpfMask(value);
    }

    /**
     * Detecta se um número de telefone contém DDI
     */
    detectDDI(phoneValue: string): boolean {
        if (!phoneValue) return false;

        const numbers = phoneValue.replace(/\D/g, '');

        // Prioriza números brasileiros (DDI 55)
        if (numbers.startsWith('55') && numbers.length >= 12) {
            return true;
        }
        // Tenta detectar DDI de 3 dígitos
        if (numbers.length >= 10 && /^[2-9]\d{2}/.test(numbers)) {
            const potentialDDI = numbers.substring(0, 3);
            const potentialRest = numbers.substring(3);
            if (potentialRest.length >= 7) {
                return true;
            }
        }
        // Tenta detectar DDI de 2 dígitos (exceto 55)
        if (numbers.length >= 9 && /^[1-9]\d/.test(numbers) && !numbers.startsWith('55')) {
            const potentialDDI = numbers.substring(0, 2);
            const potentialRest = numbers.substring(2);
            if (potentialRest.length >= 7 || (potentialRest.length >= 4 && numbers.length >= 6)) {
                const ddiNum = parseInt(potentialDDI, 10);
                if (ddiNum >= 10 && ddiNum <= 99) {
                    return true;
                }
            }
        }
        // Tenta detectar DDI de 1 dígito
        if (numbers.length >= 8 && /^[17]/.test(numbers)) {
            const potentialDDI = numbers.substring(0, 1);
            const potentialRest = numbers.substring(1);
            if (potentialRest.length >= 7) {
                return true;
            }
        }

        return false;
    }

    /**
     * Lista de parâmetros aceitáveis para tracking
     */
    getAcceptedTrackingParams(): string[] {
        return [
            // UTM Parameters
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_term',
            'utm_content',
            'utm_id',
            'utm_cid',

            // Google Ads
            'gclid',
            'gclsrc',
            'dclid',
            'wbraid',
            'gbraid',

            // Facebook/Meta
            'fbclid',
            'msclkid',

            // Outros parâmetros comuns
            'src',
            'sck',
            'ref',
            'source',
            'referrer',
            'referer',
            'affiliate_id',
            'affiliate',
            'aff_id',
            'partner',
            'partner_id',
            'campaign',
            'campaign_id',
            'ad_id',
            'adset_id',
            'adgroup_id',
            'keyword',
            'placement',
            'network',
            'device',
            'device_type',
            'creative',
            'creative_id',
            'ad_position',
            'ad_format',
            'click_id',
            'clickid',
            'transaction_id',
            'sub_id',
            'subid',
            'sub_id1',
            'sub_id2',
            'sub_id3',
            'sub_id4',
            'sub_id5',
        ];
    }
}
